<script lang="ts">
  import { format, isToday, isYesterday } from 'date-fns';
  import * as ContextMenu from '$lib/components/ui/context-menu';
  import { Button } from '$lib/components/ui/button';
  import Avatar from '../Avatar.svelte';
  import ReadReceiptAvatars from '../ReadReceiptAvatars.svelte';
  import WhatsAppIcon from '../WhatsAppIcon.svelte';
  import AttachmentGrid from '../Messaging/AttachmentGrid.svelte';
  import ReactionsRow from '../Messaging/ReactionsRow.svelte';
  import DeleteConfirmDialog from '../Messaging/DeleteConfirmDialog.svelte';
  import MessageActionsMenu from '../Messaging/MessageActionsMenu.svelte';
  import type { Comment } from './api';
  import type { MessagingUser } from '../Messaging/api';

  export let comment: Comment;
  // Consecutive comments from the same author within GROUP_WINDOW_MS of the previous one collapse
  // onto a single avatar/name (see CommentList's grouping logic) -- this is only the header, the
  // body/attachment below always render.
  export let isInSequence: boolean = true;
  // Comments are always multi-reader (subject + however many coaches) -- there's no DM-style
  // single-boolean case here, unlike Messaging. Resolved by the caller (CommentList) to "this
  // reader's most recent read comment", same dedup as the Messaging avatar list.
  export let readers: MessagingUser[] = [];
  export let currentUserId: number;

  export let onReply: (comment: Comment) => void = () => {};
  export let onEdit: (comment: Comment) => void = () => {};
  export let onDelete: (comment: Comment) => void = () => {};
  export let onReact: (comment: Comment, emoji: string) => void = () => {};
  export let onPin: (comment: Comment) => void = () => {};
  export let onUnpin: (comment: Comment) => void = () => {};
  export let onForward: (comment: Comment) => void = () => {};
  export let onJumpTo: (commentId: string) => void = () => {};

  let openDeleteDialog = false;
  let contextMenuOpen = false;
  let contextMenuTriggerEl: HTMLDivElement;

  $: mine = comment.author?.id === currentUserId;

  $: showHeader = !isInSequence || (comment.edited_at && !comment.deleted_at);

  // Lets the "..." button open the exact same menu a right-click/long-press on the row would,
  // rather than maintaining a second, separately-wired menu -- same technique used by
  // CalendarActivityCard.svelte's own "..." overlay buttons. Event modifiers (|stopPropagation)
  // only work on native DOM elements, not on a component's forwarded `on:click` -- Button is the
  // latter, so the stop happens here instead.
  function openContextMenu(e: MouseEvent) {
    e.stopPropagation();
    contextMenuTriggerEl?.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, clientX: e.clientX, clientY: e.clientY }));
  }

  function requestDelete() {
    contextMenuOpen = false;
    openDeleteDialog = true;
  }

  function timeLabel(iso: string): string {
    const d = new Date(iso);
    if (isToday(d)) return format(d, 'h:mm a');
    if (isYesterday(d)) return 'Yesterday, ' + format(d, 'h:mm a');
    return format(d, d.getFullYear() === new Date().getFullYear() ? 'MMM d, h:mm a' : 'MMM d, yyyy');
  }
</script>

<ContextMenu.Root bind:open={contextMenuOpen} portal="#shadcn-ui-dialog-target">
  <ContextMenu.Trigger>
    <div
      bind:this={contextMenuTriggerEl}
      id="comment-{comment.id}"
      class="group mx-2 flex gap-3 rounded-md px-2 {showHeader ? 'pt-3' : 'pt-0.5'} pb-0.5 transition-colors hover:bg-muted/60"
    >
      <div class="w-8 shrink-0">
        {#if showHeader}
          <Avatar
            profile_photo={comment.author?.profile_photo}
            first_name={comment.author?.first_name}
            last_name={comment.author?.last_name}
            username={comment.author?.username}
            shape="circle"
            pad_right={false}
          />
        {/if}
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-baseline gap-2">
          {#if showHeader}
            <span class="text-sm font-medium text-foreground">{comment.author ? `${comment.author.first_name} ${comment.author.last_name}` : 'Unknown'}</span>
            <span class="shrink-0 text-[11px] text-muted-foreground">{timeLabel(comment.created_at)}</span>
            {#if comment.edited_at && !comment.deleted_at}<span class="shrink-0 text-[11px] text-muted-foreground">(edited)</span>{/if}
            {#if comment.pinned_at}<i class="fa-solid fa-thumbtack text-[11px] text-muted-foreground" title="Pinned"></i>{/if}
          {/if}
        </div>

        {#if comment.deleted_at}
          <p class="m-0 mt-0.5 italic text-muted-foreground text-xs">This comment was deleted</p>
        {:else}
          {#if comment.forwarded_from}
            <div class="mb-1 mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <i class="fa-solid fa-share"></i>
              <span>Forwarded from {comment.forwarded_from.first_name} {comment.forwarded_from.last_name}</span>
            </div>
          {/if}
          {#if comment.reply_to}
            <button
              type="button"
              class="mb-1 mt-0.5 block w-full max-w-sm rounded border-l-2 border-border bg-muted/60 px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted"
              on:click={() => comment.reply_to && onJumpTo(comment.reply_to.id)}
            >
              {#if comment.reply_to.deleted}
                <span class="italic text-xs">Original comment was deleted</span>
              {:else}
                <span class="font-medium">{comment.reply_to.author ? `${comment.reply_to.author.first_name} ${comment.reply_to.author.last_name}` : 'Unknown'}: </span>
                <span class="line-clamp-2 break-words">{comment.reply_to.body ?? '[attachment]'}</span>
              {/if}
            </button>
          {/if}
          {#if comment.attachments.length > 0}
            <div class="mb-1 mt-1 flex flex-wrap gap-1">
              <AttachmentGrid attachments={comment.attachments} />
            </div>
          {/if}
          {#if comment.body}
            <p class="m-0 mt-0.5 whitespace-pre-wrap break-words text-sm text-foreground">{comment.body}</p>
          {/if}
        {/if}

        {#if comment.reactions.length > 0}
          <div class="mt-1">
            <ReactionsRow reactions={comment.reactions} {currentUserId} onReact={(emoji) => onReact(comment, emoji)} />
          </div>
        {/if}

        {#if readers.length > 0}
          <div class="mt-1">
            <ReadReceiptAvatars {readers} />
          </div>
        {/if}
      </div>
      {#if !comment.deleted_at}
        {#if comment.via_whatsapp}
          <WhatsAppIcon size={12} class="ml-auto self-start mt-1" />
        {/if}
        <Button
          size="icon"
          variant="ghost"
          class="{comment.via_whatsapp ? '' : 'ml-auto'} h-6 w-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
          on:click={openContextMenu}
        >
          <i class="fa-solid fa-ellipsis text-xs"></i>
        </Button>
      {/if}
    </div>
  </ContextMenu.Trigger>
  {#if !comment.deleted_at}
    <ContextMenu.Content>
      <MessageActionsMenu
        reactions={comment.reactions}
        {currentUserId}
        {mine}
        pinned={!!comment.pinned_at}
        onReply={() => onReply(comment)}
        onForward={() => onForward(comment)}
        onPin={() => onPin(comment)}
        onUnpin={() => onUnpin(comment)}
        onReact={(emoji) => onReact(comment, emoji)}
        onEdit={() => onEdit(comment)}
        onRequestDelete={requestDelete}
      />
    </ContextMenu.Content>
  {/if}
</ContextMenu.Root>

<DeleteConfirmDialog bind:open={openDeleteDialog} entityNoun="comment" containerNoun="thread" onConfirm={() => onDelete(comment)} />
